import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Autocomplete,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Client, Contract, Invoice } from '../../types';
import { InvoiceFormData } from '../../services/invoiceService';
import { clientService } from '../../services/clientService';
import invoiceService from '../../services/invoiceService';
import clienteCreditoService, { ClienteCredito } from '../../services/clienteCreditoService';
import { AccountBalanceWallet } from '@mui/icons-material';
import paymentService, { PaymentSummary } from '../../services/paymentService';
import GatewaySelect from '../selects/GatewaySelect';
import ContractsActiveSelect from '../selects/ContractsActiveSelect';
import ClientSelect from '../selects/ClientSelect';
import { contractService } from '../../services/contractService';
import { configurationService } from '../../services/configurationService';
import { Gateway } from '../../services/gatewayService';
import Swal from 'sweetalert2';
import { formatarValorOnInput } from '../../utils/formatToView';

interface InvoiceMultipleFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  invoice?: Invoice | null;
  title?: string;
  clientId?: number; // Cliente pré-selecionado (vindo da página do cliente)
  contractId?: number; // Contrato pré-selecionado (vindo da página do contrato)
  faturaVenceSabado?: 'ativo' | 'inativo'; // Configuração para ajustar vencimento no sábado
  isEditMode?: boolean; // Modo de edição (oculta gateway)
  selectedInvoicesData?: Invoice[]; // Dados das faturas selecionadas para edição em massa
}

// Interface para mensalidade individual
interface Mensalidade {
  id: number;
  data_vencimento: string;
  valor: number;
  acrescimo: number;
  desconto: number;
  descricao: string;
  valorFormatado: string;
  acrescimoFormatado: string;
  descontoFormatado: string;
  data_inicial?: string | null;
  data_final?: string | null;
}

// Função para obter data atual no formato YYYY-MM-DD
const getCurrentDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Função para adicionar meses a uma data
const adicionarMeses = (dataString: string, meses: number): string => {
  const [year, month, day] = dataString.split('-').map(num => parseInt(num, 10));
  const date = new Date(year, month - 1, day);
  date.setMonth(date.getMonth() + meses);

  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  const newDay = String(date.getDate()).padStart(2, '0');

  return `${newYear}-${newMonth}-${newDay}`;
};

// Função para ajustar data de vencimento quando cair no sábado ou domingo
const ajustarDataVencimento = (dataString: string, faturaVenceSabado: 'ativo' | 'inativo' = 'inativo'): string => {
  const [year, month, day] = dataString.split('-').map(num => parseInt(num, 10));
  const date = new Date(year, month - 1, day);

  // Verificar se é sábado (6 = sábado)
  if (date.getDay() === 6) {
    if (faturaVenceSabado === 'inativo') {
      // Se configuração for inativa, adicionar 2 dias para cair na segunda
      date.setDate(date.getDate() + 2);
    }
    // Se configuração for ativa, manter no sábado
  }

  // Verificar se é domingo (0 = domingo) - sempre ajustar para segunda
  if (date.getDay() === 0) {
    // Adicionar 1 dia para cair na segunda-feira
    date.setDate(date.getDate() + 1);
  }

  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  const newDay = String(date.getDate()).padStart(2, '0');

  return `${newYear}-${newMonth}-${newDay}`;
};

const InvoiceMultipleForm: React.FC<InvoiceMultipleFormProps> = ({
  open,
  onClose,
  onSubmit,
  invoice,
  title = 'Nova Fatura',
  clientId,
  contractId,
  faturaVenceSabado = 'inativo',
  isEditMode = false,
  selectedInvoicesData
}) => {
  const [formData, setFormData] = useState<InvoiceFormData>({
    cliente_id: 0,
    contrato_id: null, // Inicializar como null para mostrar "Selecione um contrato"
    data_vencimento: getCurrentDate(),
    tipo: 'mensalidade',
    valor: 0,
    acrescimo: 0,
    desconto: 0,
    descricao: '',
    mes_referencia: invoiceService.getCurrentReferenceMonth(),
    gateway_id: null,
    naogeranfcom: false
  });

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Estados para valores formatados em moeda
  const [valorFormatado, setValorFormatado] = useState('0,00');
  const [acrescimoFormatado, setAcrescimoFormatado] = useState('0,00');
  const [descontoFormatado, setDescontoFormatado] = useState('0,00');

  // Estado para resumo de pagamentos
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [availableCredits, setAvailableCredits] = useState<ClienteCredito[]>([]);
  const [creditDiscount, setCreditDiscount] = useState<number>(0);

  // Estados para múltiplas mensalidades
  const [qtdeMensalidades, setQtdeMensalidades] = useState<number>(1);
  const [mensalidades, setMensalidades] = useState<Mensalidade[]>([]);

  // Estados para detecção automática de primeira fatura
  const [isFirstInvoice, setIsFirstInvoice] = useState<boolean | null>(null); // null = não verificado
  const [infoProporcional, setInfoProporcional] = useState<string>(''); // Info para exibir no Alert

  // Dados do contrato para (re)geração de mensalidades ao alterar qtde
  const [dadosContratoFaturamento, setDadosContratoFaturamento] = useState<{
    proximoVencimento: string; // YYYY-MM-DD
    isPrimeiraFatura: boolean;
    dataContratoStr: string | null;
    valorContrato: number;
    valorAcrescimo: number;
    valorDesconto: number;
    descricao: string;
    isPrePago: boolean;
    diaVencimento: number;
  } | null>(null);

  // Parâmetro: dias mínimos para proporcional (carregado das configurações)
  const [diasMinimosProporcional, setDiasMinimosProporcional] = useState<number>(0);

  // Função auxiliar para criar Date a partir de string YYYY-MM-DD sem problemas de fuso horário
  const criarDataSegura = (dataString: string): Date | null => {
    if (!dataString || dataString === '') return null;

    try {
      // Separar os componentes da data
      const [year, month, day] = dataString.split('-').map(num => parseInt(num, 10));

      // Validar se os valores são válidos
      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      if (year < 1900 || year > 2100) return null;
      if (month < 1 || month > 12) return null;
      if (day < 1 || day > 31) return null;

      // Criar Date usando construtor com componentes (evita problemas de fuso horário)
      const date = new Date(year, month - 1, day); // month é 0-based

      // Verificar se a data criada é válida
      if (isNaN(date.getTime())) return null;

      // Verificar se os componentes correspondem (previne datas como 31/02)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
      }

      return date;
    } catch (error) {
      console.error('Erro ao criar data segura:', error);
      return null;
    }
  };

  // Função auxiliar para formatar Date para string YYYY-MM-DD
  const formatarDataSegura = (date: Date): string => {
    if (!date || isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Função para formatar valor para moeda brasileira
  const formatarMoeda = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };

  // Função para gerar mensalidades
  const gerarMensalidades = (quantidade: number, dataInicial: string, valorBase: number, acrescimoBase: number, descontoBase: number, descricaoBase: string) => {
    const novasMensalidades: Mensalidade[] = [];

    for (let i = 0; i < quantidade; i++) {
      let dataVencimento = adicionarMeses(dataInicial, i);

      // Aplicar ajuste de vencimento no sábado se configurado
      dataVencimento = ajustarDataVencimento(dataVencimento, faturaVenceSabado);

      const mesReferencia = `${String(new Date(dataVencimento).getMonth() + 1).padStart(2, '0')}-${new Date(dataVencimento).getFullYear()}`;

      novasMensalidades.push({
        id: i + 1,
        data_vencimento: dataVencimento,
        valor: valorBase,
        acrescimo: acrescimoBase,
        desconto: descontoBase,
        descricao: `${descricaoBase}`,
        data_inicial: undefined,
        data_final: undefined,
        //descricao: `${descricaoBase} - ${mesReferencia}`,
        valorFormatado: formatarMoeda(valorBase),
        acrescimoFormatado: formatarMoeda(acrescimoBase),
        descontoFormatado: formatarMoeda(descontoBase)
      });
    }

    return novasMensalidades;
  };

  // Função para carregar resumo de pagamentos da fatura
  const loadPaymentSummary = async (invoiceId: number, valor: number, acrescimo: number, desconto: number) => {
    if (!invoiceId) return;

    try {
      setLoadingPayments(true);
      const summary = await paymentService.getPaymentSummary(invoiceId, valor, acrescimo, desconto);
      setPaymentSummary(summary);
    } catch (error) {
      console.error('Erro ao carregar resumo de pagamentos:', error);
      setPaymentSummary(null);
    } finally {
      setLoadingPayments(false);
    }
  };



  // Preencher formulário quando editando ou quando há contexto pré-definido
  useEffect(() => {
    if (invoice && open) {
      // Editando fatura existente
      const valorNum = Number(invoice.valor) || 0;
      const acrescimoNum = Number(invoice.acrescimo) || 0;
      const descontoNum = Number(invoice.desconto) || 0;

      // Validar e formatar data de vencimento
      let dataVencimento = '';
      if (invoice.data_vencimento) {
        try {
          // Primeiro tentar como string já formatada (YYYY-MM-DD)
          if (typeof invoice.data_vencimento === 'string' && invoice.data_vencimento.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dataVencimento = invoice.data_vencimento;
          } else {
            // Se não, tentar converter de ISO ou outro formato
            const date = new Date(invoice.data_vencimento);
            if (!isNaN(date.getTime())) {
              dataVencimento = formatarDataSegura(date);
            }
          }
        } catch (error) {
          console.error('Erro ao processar data de vencimento:', error);
          dataVencimento = '';
        }
      }

      setFormData({
        cliente_id: invoice.cliente_id,
        contrato_id: invoice.contrato_id || null,
        data_vencimento: dataVencimento,
        tipo: invoice.tipo,
        valor: valorNum,
        acrescimo: acrescimoNum,
        desconto: descontoNum,
        descricao: invoice.descricao,
        mes_referencia: invoice.mes_referencia,
        gateway_id: invoice.gateway_id || null
      });

      // Formatar valores para exibição
      setValorFormatado(formatarMoeda(valorNum));
      setAcrescimoFormatado(formatarMoeda(acrescimoNum));
      setDescontoFormatado(formatarMoeda(descontoNum));

      // Definir cliente selecionado (será carregado pelo ClientSelect)
      setSelectedClient(null);

      // Carregar resumo de pagamentos se existir ID da fatura
      if (invoice.id) {
        loadPaymentSummary(invoice.id, valorNum, acrescimoNum, descontoNum);
      }

      // Para edição, sempre usar quantidade 1
      setQtdeMensalidades(1);
      // Não limpar mensalidades em modo de edição, pois os dados vêm das faturas selecionadas
      if (!isEditMode) {
        setMensalidades([]);
      }
    } else if (open) {
      // Nova fatura - considerar contexto (clientId/contractId)
      const initialClientId = clientId || 0;
      const initialContractId = contractId || null;

      setFormData({
        cliente_id: initialClientId,
        contrato_id: initialContractId,
        data_vencimento: getCurrentDate(),
        tipo: 'mensalidade',
        valor: 0,
        acrescimo: 0,
        desconto: 0,
        descricao: '',
        mes_referencia: invoiceService.getCurrentReferenceMonth(),
        gateway_id: null
      });

      // Resetar valores formatados
      setValorFormatado('0,00');
      setAcrescimoFormatado('0,00');
      setDescontoFormatado('0,00');

      // Se clientId foi fornecido, definir como selecionado
      if (clientId) {
        setSelectedClient({ id: clientId } as Client);
      } else {
        setSelectedClient(null);
      }

      // Resetar quantidade de mensalidades
      setQtdeMensalidades(1);
      // Não limpar mensalidades em modo de edição
      if (!isEditMode) {
        setMensalidades([]);
      }
    }
  }, [invoice, open, clientId, contractId, isEditMode]);

  // Carregar créditos disponíveis do cliente
  useEffect(() => {
    const loadCredits = async () => {
      // Carregar créditos apenas se for criação (invoice == null) e tiver cliente selecionado
      if (!invoice && !isEditMode && formData.cliente_id > 0) {
        try {
          const creditos = await clienteCreditoService.getCreditosDisponiveis(formData.cliente_id);
          setAvailableCredits(creditos);
        } catch (error) {
          console.error('Erro ao carregar créditos disponíveis:', error);
          setAvailableCredits([]);
        }
      } else {
        setAvailableCredits([]);
      }
    };
    
    // Resetar seleção de créditos
    setFormData(prev => ({ ...prev, selectedCredits: [] }));
    setCreditDiscount(0);
    
    loadCredits();
  }, [formData.cliente_id, invoice, isEditMode]);

  // Efeito para inicializar mensalidades quando o formulário abrir
  useEffect(() => {
    // Não executar em modo de edição, pois os dados vêm das faturas selecionadas
    if (open && !isEditMode && qtdeMensalidades >= 1 && mensalidades.length === 0) {
      // Gerar mensalidade inicial apenas se não houver mensalidades
      const dataInicial = formData.data_vencimento || getCurrentDate();
      const valorBase = formData.valor || 0;
      const acrescimoBase = formData.acrescimo || 0;
      const descontoBase = formData.desconto || 0;
      const descricaoBase = formData.descricao || 'Assinatura de Servicos de Comunicação Multimidia'; // Descrição padrão se estiver vazia

      const mensalidadeInicial = gerarMensalidades(
        qtdeMensalidades,
        dataInicial,
        valorBase,
        acrescimoBase,
        descontoBase,
        descricaoBase
      );
      setMensalidades(mensalidadeInicial);
    }
  }, [open, isEditMode, qtdeMensalidades, mensalidades.length]);

  // Efeito para carregar dados das faturas selecionadas em modo de edição
  useEffect(() => {
    if (open && isEditMode && selectedInvoicesData && selectedInvoicesData.length > 0) {
      // Converter faturas selecionadas para o formato de mensalidades
      const mensalidadesFromInvoices = selectedInvoicesData
        .sort((a, b) => b.id - a.id) // Ordenar por ID decrescente
        .map((invoice, index) => {
          // Converter data ISO para formato yyyy-MM-dd
          const dataVencimento = invoice.data_vencimento ?
            new Date(invoice.data_vencimento).toISOString().split('T')[0] :
            getCurrentDate();

          return {
            id: invoice.id,
            data_vencimento: dataVencimento,
            valor: Number(invoice.valor) || 0,
            acrescimo: Number(invoice.acrescimo) || 0,
            desconto: Number(invoice.desconto) || 0,
            descricao: invoice.descricao || 'Mensalidade',
            valorFormatado: formatarMoeda(Number(invoice.valor) || 0),
            acrescimoFormatado: formatarMoeda(Number(invoice.acrescimo) || 0),
            descontoFormatado: formatarMoeda(Number(invoice.desconto) || 0)
          };
        });

      setMensalidades(mensalidadesFromInvoices);

      // Preencher apenas alguns dados do formulário com a primeira fatura (para referência)
      // Não preencher tipo e contrato, pois serão definidos pelo usuário
      if (selectedInvoicesData.length > 0) {
        const firstInvoice = selectedInvoicesData[0];
        setFormData({
          cliente_id: firstInvoice.cliente_id,
          contrato_id: undefined, // Não preencher automaticamente
          tipo: 'mensalidade' as const, // Valor padrão, não preencher com dados da fatura
          valor: Number(firstInvoice.valor) || 0,
          acrescimo: Number(firstInvoice.acrescimo) || 0,
          desconto: Number(firstInvoice.desconto) || 0,
          descricao: firstInvoice.descricao || 'Mensalidade',
          data_vencimento: firstInvoice.data_vencimento,
          gateway_id: firstInvoice.gateway_id,
          mes_referencia: firstInvoice.mes_referencia || ''
        });

        setValorFormatado(formatarMoeda(Number(firstInvoice.valor) || 0));
        setAcrescimoFormatado(formatarMoeda(Number(firstInvoice.acrescimo) || 0));
        setDescontoFormatado(formatarMoeda(Number(firstInvoice.desconto) || 0));
      }
    }
  }, [open, isEditMode, selectedInvoicesData]);

  // Carregar configuração de dias mínimos proporcional
  useEffect(() => {
    const loadDiasMinimos = async () => {
      try {
        const config = await configurationService.getConfigurationByKey('dias_minimos_proporcional');
        if (config && config.valor) {
          setDiasMinimosProporcional(parseInt(config.valor) || 0);
        }
      } catch (err) {
        // Config não existe ainda, usar default 0
      }
    };
    if (open) loadDiasMinimos();
  }, [open]);

  // ========== FUNÇÃO PARA GERAR MENSALIDADES COM PERÍODOS ==========
  const gerarMensalidadesInteligentes = (
    quantidade: number,
    proximoVencimento: string, // YYYY-MM-DD do próximo dia_vencimento
    isPrimeiraFatura: boolean,
    dataContratoStr: string | null, // YYYY-MM-DD
    valorMensal: number,
    acrescimoBase: number,
    descontoBase: number,
    descricaoBase: string,
    isPrePago: boolean,
    diasMinimos: number = 0 // Dias mínimos para proporcional (0 = desativado)
  ): Mensalidade[] => {
    const novasMensalidades: Mensalidade[] = [];

    const fmtBR = (dateStr: string): string => {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    };

    const fmtBRDate = (d: Date): string =>
      `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

    const formatarDataSegura = (d: Date): string => {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    if (isPrimeiraFatura && dataContratoStr) {
      // ===== PRIMEIRA FATURA: incluir proporcional =====
      const dataContrato = criarDataSegura(dataContratoStr);
      const proxVenc = criarDataSegura(proximoVencimento);

      if (dataContrato && proxVenc) {
        // Período proporcional: data_contrato até (proximoVencimento - 1 dia)
        const periodoFim = new Date(proxVenc);
        periodoFim.setDate(periodoFim.getDate() - 1);

        const diffDays = Math.ceil((proxVenc.getTime() - dataContrato.getTime()) / (1000 * 60 * 60 * 24));
        const valorProporcional = diffDays > 0 && diffDays < 30
          ? (valorMensal / 30) * diffDays
          : valorMensal;

        // Verificar se deve embutir proporcional na próxima mensalidade
        const embutirProporcional = diasMinimos > 0 && diffDays > 0 && diffDays <= diasMinimos;

        if (embutirProporcional) {
          // EMBUTIR: proporcional + primeira mensalidade cheia = uma única fatura
          const valorEmbutido = valorProporcional + valorMensal;
          const periodStartStr = proximoVencimento; // = próximo dia_vencimento
          const periodEndNextStr = adicionarMeses(proximoVencimento, 1);
          const periodEndDate = criarDataSegura(periodEndNextStr);
          if (periodEndDate) periodEndDate.setDate(periodEndDate.getDate() - 1);

          // Vencimento: pré-pago = proximoVencimento, pós-pago = próximo mês
          let vencimentoEmbutido = isPrePago ? proximoVencimento : periodEndNextStr;
          vencimentoEmbutido = ajustarDataVencimento(vencimentoEmbutido, faturaVenceSabado);

          // Período: data_contrato até fim da primeira mensalidade cheia
          const periodoEmbutido = `${fmtBRDate(dataContrato)} à ${periodEndDate ? fmtBRDate(periodEndDate) : ''}`;

          novasMensalidades.push({
            id: 1,
            data_vencimento: vencimentoEmbutido,
            valor: valorEmbutido,
            acrescimo: acrescimoBase,
            desconto: descontoBase,
            descricao: `${descricaoBase} (${periodoEmbutido})`,
            data_inicial: dataContrato ? formatarDataSegura(dataContrato) : undefined,
            data_final: periodEndDate ? formatarDataSegura(periodEndDate) : undefined,
            valorFormatado: formatarMoeda(valorEmbutido),
            acrescimoFormatado: formatarMoeda(acrescimoBase),
            descontoFormatado: formatarMoeda(descontoBase)
          });

          // Mensalidades subsequentes (a partir do 2º período cheio)
          for (let i = 1; i < quantidade; i++) {
            const pStartStr = adicionarMeses(proximoVencimento, i);
            const pEndNextStr = adicionarMeses(proximoVencimento, i + 1);
            const pEndDate = criarDataSegura(pEndNextStr);
            if (pEndDate) pEndDate.setDate(pEndDate.getDate() - 1);

            let vencStr = isPrePago ? pStartStr : pEndNextStr;
            vencStr = ajustarDataVencimento(vencStr, faturaVenceSabado);

            const pStr = `${fmtBR(pStartStr)} à ${pEndDate ? fmtBRDate(pEndDate) : ''}`;

            novasMensalidades.push({
              id: i + 1,
              data_vencimento: vencStr,
              valor: valorMensal,
              acrescimo: acrescimoBase,
              desconto: descontoBase,
              descricao: `${descricaoBase} (${pStr})`,
              data_inicial: pStartStr,
              data_final: pEndDate ? formatarDataSegura(pEndDate) : undefined,
              valorFormatado: formatarMoeda(valorMensal),
              acrescimoFormatado: formatarMoeda(acrescimoBase),
              descontoFormatado: formatarMoeda(descontoBase)
            });
          }

        } else {
          // NORMAL: gerar proporcional separada
          // Pré-pago: vencimento = data_contrato | Pós-pago: vencimento = proximoVencimento
          let vencimento1 = isPrePago ? dataContratoStr : proximoVencimento;
          vencimento1 = ajustarDataVencimento(vencimento1, faturaVenceSabado);

          const periodo1 = `${fmtBRDate(dataContrato)} à ${fmtBRDate(periodoFim)}`;

          novasMensalidades.push({
            id: 1,
            data_vencimento: vencimento1,
            valor: valorProporcional,
            acrescimo: acrescimoBase,
            desconto: descontoBase,
            descricao: `${descricaoBase} (${periodo1})`,
            data_inicial: dataContrato ? formatarDataSegura(dataContrato) : undefined,
            data_final: periodoFim ? formatarDataSegura(periodoFim) : undefined,
            valorFormatado: formatarMoeda(valorProporcional),
            acrescimoFormatado: formatarMoeda(acrescimoBase),
            descontoFormatado: formatarMoeda(descontoBase)
          });

          // Mensalidades subsequentes (valor integral, com período)
          for (let i = 0; i < quantidade - 1; i++) {
            const periodStartStr = adicionarMeses(proximoVencimento, i);
            const periodEndNextStr = adicionarMeses(proximoVencimento, i + 1);
            const periodEndDate = criarDataSegura(periodEndNextStr);
            if (periodEndDate) periodEndDate.setDate(periodEndDate.getDate() - 1);

            // Pré-pago: vencimento = período start | Pós-pago: vencimento = período end + 1 dia (= próximo mês)
            let vencimentoStr = isPrePago ? periodStartStr : periodEndNextStr;
            vencimentoStr = ajustarDataVencimento(vencimentoStr, faturaVenceSabado);

            const periodoStr = `${fmtBR(periodStartStr)} à ${periodEndDate ? fmtBRDate(periodEndDate) : ''}`;

            novasMensalidades.push({
              id: i + 2,
              data_vencimento: vencimentoStr,
              valor: valorMensal,
              acrescimo: acrescimoBase,
              desconto: descontoBase,
              descricao: `${descricaoBase} (${periodoStr})`,
              data_inicial: periodStartStr,
              data_final: periodEndDate ? formatarDataSegura(periodEndDate) : undefined,
              valorFormatado: formatarMoeda(valorMensal),
              acrescimoFormatado: formatarMoeda(acrescimoBase),
              descontoFormatado: formatarMoeda(descontoBase)
            });
          }
        }
      }
    } else {
      // ===== NÃO É PRIMEIRA FATURA ou sem data_contrato =====
      // Gerar a partir do proximoVencimento (calculado da última fatura + 1 mês)
      for (let i = 0; i < quantidade; i++) {
        // Vencimento desta fatura
        const vencimentoBase = adicionarMeses(proximoVencimento, i);

        let periodoInicioDate: Date | null;
        let periodoFimDate: Date | null;

        if (isPrePago) {
          // PRÉ-PAGO: paga antecipado, período = vencimento até próximo mês - 1 dia
          // Ex: Vencimento 20/06 → Período 20/06 à 19/07
          periodoInicioDate = criarDataSegura(vencimentoBase);
          const proximoMes = adicionarMeses(vencimentoBase, 1);
          periodoFimDate = criarDataSegura(proximoMes);
          if (periodoFimDate) periodoFimDate.setDate(periodoFimDate.getDate() - 1);
        } else {
          // PÓS-PAGO: paga após uso, período = mês anterior ao vencimento até véspera do vencimento
          // Ex: Vencimento 20/06 → Período 20/05 à 19/06
          periodoInicioDate = criarDataSegura(vencimentoBase);
          if (periodoInicioDate) periodoInicioDate.setMonth(periodoInicioDate.getMonth() - 1);
          periodoFimDate = criarDataSegura(vencimentoBase);
          if (periodoFimDate) periodoFimDate.setDate(periodoFimDate.getDate() - 1);
        }

        let vencimentoStr = ajustarDataVencimento(vencimentoBase, faturaVenceSabado);

        const periodoStr = `${periodoInicioDate ? fmtBRDate(periodoInicioDate) : fmtBR(vencimentoBase)} à ${periodoFimDate ? fmtBRDate(periodoFimDate) : ''}`;

        novasMensalidades.push({
          id: i + 1,
          data_vencimento: vencimentoStr,
          valor: valorMensal,
          acrescimo: acrescimoBase,
          desconto: descontoBase,
          descricao: `${descricaoBase} (${periodoStr})`,
          data_inicial: periodoInicioDate ? formatarDataSegura(periodoInicioDate) : undefined,
          data_final: periodoFimDate ? formatarDataSegura(periodoFimDate) : undefined,
          valorFormatado: formatarMoeda(valorMensal),
          acrescimoFormatado: formatarMoeda(acrescimoBase),
          descontoFormatado: formatarMoeda(descontoBase)
        });
      }
    }

    return novasMensalidades;
  };

  // Efeito para preencher valor e descrição automaticamente quando contrato for selecionado
  useEffect(() => {
    // Só preencher automaticamente se não estivermos editando uma fatura existente ou em modo de edição em massa
    if (!invoice && !isEditMode && formData.contrato_id) {
      // Carregar dados do contrato selecionado
      const loadContractData = async () => {
        try {
          const response = await contractService.getByClientId(formData.cliente_id);
          if (response.success) {
            const contracts = response.data || [];
            const selectedContract = contracts.find(c => c.id === formData.contrato_id);

            if (selectedContract && selectedContract.valor_contratado !== undefined) {
              const valorContrato = Number(selectedContract.valor_contratado) || 0;
              const valorAcrescimo = Number(selectedContract.acrescimo) || 0;
              const valorDesconto = Number(selectedContract.desconto) || 0;
              const isPrePago = selectedContract.modalidade === 'pre-pago';
              const diaVencimento = selectedContract.dia_vencimento || 10;

              // ========== VERIFICAR FATURAS EXISTENTES DO CONTRATO ==========
              let isPrimeiraFatura = true;
              let ultimaFaturaMensalidade: any = null;
              try {
                const invoicesResponse = await invoiceService.getInvoicesByContract(formData.contrato_id!);
                const faturasExistentes = (invoicesResponse.data || []) as any[];
                const mensalidadesExistentes = faturasExistentes.filter(
                  (f: any) => f.tipo === 'mensalidade' && f.status !== 'cancelado'
                );
                isPrimeiraFatura = mensalidadesExistentes.length === 0;

                if (!isPrimeiraFatura && mensalidadesExistentes.length > 0) {
                  ultimaFaturaMensalidade = mensalidadesExistentes
                    .sort((a: any, b: any) => new Date(b.data_vencimento).getTime() - new Date(a.data_vencimento).getTime())[0];
                }
              } catch (err) {
                console.warn('⚠️ [INVOICE FORM] Não foi possível verificar faturas existentes:', err);
              }
              setIsFirstInvoice(isPrimeiraFatura);

              // ========== CALCULAR PRÓXIMO VENCIMENTO ==========
              let proximoVencimento = '';
              let dataContratoStr: string | null = null;

              const fmtBR = (d: string) => { const [y, m, dd] = d.split('-'); return `${dd}/${m}/${y}`; };

              if (ultimaFaturaMensalidade) {
                // JÁ EXISTEM FATURAS: próximo vencimento = última fatura + 1 mês
                const ultimaDataStr = typeof ultimaFaturaMensalidade.data_vencimento === 'string' && ultimaFaturaMensalidade.data_vencimento.includes('T')
                  ? ultimaFaturaMensalidade.data_vencimento.split('T')[0]
                  : String(ultimaFaturaMensalidade.data_vencimento);
                const ultimaData = criarDataSegura(ultimaDataStr);

                if (ultimaData) {
                  let proxMes = ultimaData.getMonth() + 1;
                  let proxAno = ultimaData.getFullYear();
                  if (proxMes > 11) { proxMes = 0; proxAno++; }
                  const ultimoDia = new Date(proxAno, proxMes + 1, 0).getDate();
                  const diaFinal = Math.min(diaVencimento, ultimoDia);
                  proximoVencimento = `${proxAno}-${String(proxMes + 1).padStart(2, '0')}-${String(diaFinal).padStart(2, '0')}`;
                  setInfoProporcional(`Última fatura: ${fmtBR(ultimaDataStr)} → Próxima: ${fmtBR(proximoVencimento)}`);
                }
              } else {
                // PRIMEIRA FATURA: próximo dia_vencimento após data_contrato
                dataContratoStr = selectedContract.data_contrato
                  ? (typeof selectedContract.data_contrato === 'string' && selectedContract.data_contrato.includes('T')
                    ? selectedContract.data_contrato.split('T')[0]
                    : String(selectedContract.data_contrato))
                  : null;
                const dataRef = dataContratoStr ? criarDataSegura(dataContratoStr) : new Date();

                if (dataRef) {
                  let ano = dataRef.getFullYear();
                  let mes = dataRef.getMonth();
                  // Se data_contrato já passou do dia_vencimento, próximo mês
                  if (dataRef.getDate() >= diaVencimento) {
                    mes++;
                    if (mes > 11) { mes = 0; ano++; }
                  }
                  const ultimoDia = new Date(ano, mes + 1, 0).getDate();
                  const diaFinal = Math.min(diaVencimento, ultimoDia);
                  proximoVencimento = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(diaFinal).padStart(2, '0')}`;

                  // Calcular info proporcional
                  if (dataContratoStr) {
                    const proxVencDate = criarDataSegura(proximoVencimento);
                    if (proxVencDate) {
                      const diffDays = Math.ceil((proxVencDate.getTime() - dataRef.getTime()) / (1000 * 60 * 60 * 24));
                      const valorProp = diffDays > 0 && diffDays < 30 ? (valorContrato / 30) * diffDays : valorContrato;
                      const periodoFim = new Date(proxVencDate); periodoFim.setDate(periodoFim.getDate() - 1);
                      const fmtD = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                      setInfoProporcional(`Primeira mensalidade proporcional: ${fmtD(dataRef)} a ${fmtD(periodoFim)} (${diffDays} dias) — R$ ${formatarMoeda(valorProp)}`);
                    }
                  }
                }
              }

              if (!proximoVencimento) {
                proximoVencimento = getCurrentDate();
              }

              // ========== ATUALIZAR DESCRIÇÃO ==========
              let novaDescricao = '';
              if (selectedContract.plano && selectedContract.plano.nome) {
                novaDescricao = selectedContract.plano.nome;
              } else if (selectedContract.titulo) {
                novaDescricao = selectedContract.titulo;
              } else {
                novaDescricao = 'Mensalidade';
              }

              // ========== SALVAR DADOS PARA REGENERAÇÃO ==========
              const dadosFat = {
                proximoVencimento,
                isPrimeiraFatura,
                dataContratoStr,
                valorContrato,
                valorAcrescimo: valorAcrescimo,
                valorDesconto: valorDesconto,
                descricao: novaDescricao,
                isPrePago,
                diaVencimento
              };
              setDadosContratoFaturamento(dadosFat);

              // ========== GERAR MENSALIDADES ==========
              const qtde = Math.max(qtdeMensalidades, 1);
              const novasMensalidades = gerarMensalidadesInteligentes(
                qtde, proximoVencimento, isPrimeiraFatura, dataContratoStr,
                valorContrato, valorAcrescimo, valorDesconto, novaDescricao, isPrePago,
                diasMinimosProporcional
              );
              setMensalidades(novasMensalidades);

              // ========== ATUALIZAR FORM ==========
              const valorPrimeira = novasMensalidades.length > 0 ? novasMensalidades[0].valor : valorContrato;
              setFormData(prev => ({
                ...prev,
                valor: valorPrimeira,
                acrescimo: valorAcrescimo,
                desconto: valorDesconto,
                gateway_id: selectedContract.gateway_id || null,
                data_vencimento: novasMensalidades.length > 0 ? novasMensalidades[0].data_vencimento : proximoVencimento,
                descricao: novaDescricao
              }));
              setValorFormatado(formatarMoeda(valorPrimeira));
              setAcrescimoFormatado(formatarMoeda(valorAcrescimo));
              setDescontoFormatado(formatarMoeda(valorDesconto));

            } else {
              setFormData(prev => ({
                ...prev,
                valor: 0,
                acrescimo: 0,
                desconto: 0,
                gateway_id: null,
                naogeranfcom: null
              }));
              setValorFormatado('0,00');
              setAcrescimoFormatado('0,00');
              setDescontoFormatado('0,00');
              setIsFirstInvoice(null);
              setInfoProporcional('');
              setDadosContratoFaturamento(null);
            }
          }
        } catch (error) {
          console.error('Erro ao carregar dados do contrato:', error);
        }
      };

      loadContractData();
    }
  }, [formData.contrato_id, formData.cliente_id, invoice, isEditMode, diasMinimosProporcional]);

  const handleInputChange = (field: keyof InvoiceFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClientChange = (client: Client | null) => {
    setSelectedClient(client);
    if (client && client.id) {
      handleInputChange('cliente_id', client.id);
      handleInputChange('contrato_id', null); // Reset contract when client changes
    } else {
      handleInputChange('cliente_id', 0);
      handleInputChange('contrato_id', null);
    }
  };

  // Handlers para campos de moeda
  const handleValorChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);

    setValorFormatado(valorFormatado);
    handleInputChange('valor', valorNumerico);
  };

  const handleAcrescimoChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);

    setAcrescimoFormatado(valorFormatado);
    handleInputChange('acrescimo', valorNumerico);
  };

  const handleDescontoChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);

    setDescontoFormatado(valorFormatado);
    handleInputChange('desconto', valorNumerico);
  };

  const handleGatewayChange = (gatewayId: number | null, gateway?: Gateway) => {
    handleInputChange('gateway_id', gatewayId);
  };

  const handleCreditSelection = (creditIds: number[]) => {
    const selectedCreditObjects = availableCredits.filter(credit => 
      creditIds.includes(credit.id)
    );
    
    const totalCreditDiscount = selectedCreditObjects.reduce((sum, credit) => {
      const saldoNumerico = Number(credit.saldo) || 0;
      return sum + saldoNumerico;
    }, 0);

    setFormData(prev => ({
      ...prev,
      selectedCredits: creditIds
    }));
    setCreditDiscount(totalCreditDiscount);
  };

  // Handler para mudança na quantidade de mensalidades
  const handleQtdeMensalidadesChange = (novaQuantidade: number) => {
    setQtdeMensalidades(novaQuantidade);

    // Se temos dados do contrato, regenerar TODAS as mensalidades com a lógica inteligente
    if (dadosContratoFaturamento) {
      const novasMensalidades = gerarMensalidadesInteligentes(
        novaQuantidade,
        dadosContratoFaturamento.proximoVencimento,
        dadosContratoFaturamento.isPrimeiraFatura,
        dadosContratoFaturamento.dataContratoStr,
        dadosContratoFaturamento.valorContrato,
        dadosContratoFaturamento.valorAcrescimo,
        dadosContratoFaturamento.valorDesconto,
        dadosContratoFaturamento.descricao,
        dadosContratoFaturamento.isPrePago,
        diasMinimosProporcional
      );
      setMensalidades(novasMensalidades);
    } else {
      // Fallback: sem dados do contrato, usar lógica simples
      const quantidadeAtual = mensalidades.length;
      if (novaQuantidade > quantidadeAtual) {
        const novasMensalidades = [...mensalidades];
        const dataInicial = formData.data_vencimento || getCurrentDate();
        const valorBase = formData.valor || 0;
        const acrescimoBase = formData.acrescimo || 0;
        const descontoBase = formData.desconto || 0;
        const descricaoBase = formData.descricao || 'Mensalidade';

        for (let i = quantidadeAtual; i < novaQuantidade; i++) {
          let dataVencimento = adicionarMeses(dataInicial, i);
          dataVencimento = ajustarDataVencimento(dataVencimento, faturaVenceSabado);

          novasMensalidades.push({
            id: i + 1,
            data_vencimento: dataVencimento,
            valor: valorBase,
            acrescimo: acrescimoBase,
            desconto: descontoBase,
            descricao: descricaoBase,
            valorFormatado: formatarMoeda(valorBase),
            acrescimoFormatado: formatarMoeda(acrescimoBase),
            descontoFormatado: formatarMoeda(descontoBase)
          });
        }
        setMensalidades(novasMensalidades);
      } else if (novaQuantidade < quantidadeAtual) {
        setMensalidades(mensalidades.slice(0, novaQuantidade));
      }
    }
  };

  // Handlers para mensalidades
  const handleMensalidadeChange = (index: number, field: keyof Mensalidade, value: any) => {
    const novasMensalidades = [...mensalidades];

    if (field === 'valorFormatado' || field === 'acrescimoFormatado' || field === 'descontoFormatado') {
      const valorFormatado = formatarValorOnInput(value);
      const valorNumerico = parsearMoeda(valorFormatado);

      novasMensalidades[index] = {
        ...novasMensalidades[index],
        [field]: valorFormatado,
        [field.replace('Formatado', '')]: valorNumerico
      };
    } else {
      novasMensalidades[index] = {
        ...novasMensalidades[index],
        [field]: value
      };
    }

    setMensalidades(novasMensalidades);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors([]);

    try {
      // Validar e submeter faturas
      const validationErrors: string[] = [];

      // Validar dados básicos
      if (!formData.cliente_id) {
        validationErrors.push('Cliente é obrigatório');
      }
      if (!formData.contrato_id) {
        validationErrors.push('Contrato é obrigatório');
      }
      if (!formData.data_vencimento) {
        validationErrors.push('Data de vencimento é obrigatória');
      }

      // Validar mensalidades
      mensalidades.forEach((mensalidade, index) => {
        if (!mensalidade.data_vencimento) {
          validationErrors.push(`Data de vencimento da mensalidade ${index + 1} é obrigatória`);
        }
        if (mensalidade.valor <= 0) {
          validationErrors.push(`Valor da mensalidade ${index + 1} deve ser maior que zero`);
        }
        if (!mensalidade.descricao) {
          validationErrors.push(`Descrição da mensalidade ${index + 1} é obrigatória`);
        }
      });

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        return;
      }

      if (isEditMode && selectedInvoicesData) {
        // Modo de edição: atualizar faturas existentes

        const faturasParaAtualizar = mensalidades.map((mensalidade, index) => {
          const faturaOriginal = selectedInvoicesData[index];
          return {
            id: faturaOriginal.id, // ID da fatura original
            cliente_id: faturaOriginal.cliente_id, // Manter cliente original
            contrato_id: formData.contrato_id || null, // Usar contrato selecionado ou null
            tipo: faturaOriginal.tipo, // Manter tipo original
            data_vencimento: mensalidade.data_vencimento,
            valor: mensalidade.valor,
            acrescimo: mensalidade.acrescimo,
            desconto: mensalidade.desconto,
            descricao: mensalidade.descricao,
            data_inicial: mensalidade.data_inicial || undefined,
            data_final: mensalidade.data_final || undefined,
            mes_referencia: `${new Date(mensalidade.data_vencimento).getFullYear()}-${String(new Date(mensalidade.data_vencimento).getMonth() + 1).padStart(2, '0')}`,
            gateway_id: faturaOriginal.gateway_id // Manter gateway original
          };
        });

        // Mostrar progresso de atualização
        await handleBulkUpdateWithProgress(faturasParaAtualizar);
      } else {
        // Modo de criação: criar novas faturas

        const faturasData: InvoiceFormData[] = mensalidades.map((mensalidade, index) => ({
          cliente_id: formData.cliente_id,
          contrato_id: formData.contrato_id || null, // Usar contrato selecionado ou null
          data_vencimento: mensalidade.data_vencimento,
          tipo: formData.tipo,
          valor: mensalidade.valor,
          acrescimo: mensalidade.acrescimo,
          desconto: mensalidade.desconto,
          descricao: mensalidade.descricao,
          data_inicial: mensalidade.data_inicial || undefined,
          data_final: mensalidade.data_final || undefined,
          mes_referencia: `${new Date(mensalidade.data_vencimento).getFullYear()}-${String(new Date(mensalidade.data_vencimento).getMonth() + 1).padStart(2, '0')}`,
          gateway_id: formData.gateway_id,
          selectedCredits: index === 0 ? formData.selectedCredits : undefined // Aplica os créditos apenas na primeira fatura
        }));

        // Mostrar progresso de criação
        await handleBulkCreateWithProgress(faturasData);
      }
    } catch (error: any) {
      console.error('Erro ao salvar fatura(s):', error);
      setErrors([error.message || 'Erro ao salvar fatura(s)']);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const basicTotal = invoiceService.calculateTotal(formData.valor, formData.acrescimo, formData.desconto);

    // Se há resumo de pagamentos, retornar valor restante
    if (paymentSummary && paymentSummary.isPartiallyPaid) {
      return paymentSummary.remainingAmount;
    }

    // Se não há pagamentos, retornar valor total básico
    return basicTotal;
  };

  const getBasicTotal = () => {
    return invoiceService.calculateTotal(formData.valor, formData.acrescimo, formData.desconto);
  };

  // Calcular total das mensalidades
  const calcularTotalMensalidades = () => {
    const total = mensalidades.reduce((acc, mensalidade) => {
      return acc + invoiceService.calculateTotal(mensalidade.valor, mensalidade.acrescimo, mensalidade.desconto);
    }, 0);
    return Math.max(0, total - creditDiscount);
  };

  // Função para criar faturas com progresso
  const handleBulkCreateWithProgress = async (faturasParaCriar: InvoiceFormData[]) => {
    try {
      // Fechar o formulário primeiro
      onClose();

      // Mostrar SweetAlert com progresso
      const { value: confirmed } = await Swal.fire({
        title: 'Criando Faturas',
        html: `
          <div id="progress-container">
            <div style="text-align: center; margin-bottom: 20px;">
              <div class="swal2-progress-bar" style="width: 100%; height: 4px; background: #e0e0e0; border-radius: 2px; overflow: hidden;">
                <div id="progress-bar" style="width: 0%; height: 100%; background: #4caf50; transition: width 0.3s;"></div>
              </div>
              <div id="progress-text" style="margin-top: 10px; font-size: 14px; color: #666;">
                Preparando criação...
              </div>
            </div>
            <div id="faturas-progress" style="max-height: 300px; overflow-y: auto; text-align: left;">
              ${faturasParaCriar.map((fatura, index) => `
                <div id="fatura-${index}" style="padding: 8px; margin: 4px 0; border-radius: 4px; background: #f5f5f5; display: flex; justify-content: space-between; align-items: center;">
                  <span style="font-weight: 500;">Fatura ${index + 1}</span>
                  <span id="status-${index}" style="color: #666;">Aguardando...</span>
                </div>
              `).join('')}
            </div>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: async () => {
          const progressBar = document.getElementById('progress-bar') as HTMLElement;
          const progressText = document.getElementById('progress-text') as HTMLElement;

          let sucessos = 0;
          let erros = 0;
          const faturasCriadas: Invoice[] = [];

          for (let i = 0; i < faturasParaCriar.length; i++) {
            const fatura = faturasParaCriar[i];
            const statusElement = document.getElementById(`status-${i}`) as HTMLElement;
            const faturaElement = document.getElementById(`fatura-${i}`) as HTMLElement;

            try {
              // Atualizar texto de progresso
              progressText.textContent = `Criando fatura ${i + 1}...`;

              // Atualizar barra de progresso
              const progress = ((i + 1) / faturasParaCriar.length) * 100;
              progressBar.style.width = `${progress}%`;

              // Atualizar status da fatura
              statusElement.textContent = 'Criando...';
              statusElement.style.color = '#ff9800';
              faturaElement.style.background = '#fff3e0';

              // Aguardar um pouco para mostrar o progresso
              await new Promise(resolve => setTimeout(resolve, 500));

              // Fazer a chamada da API
              const response = await invoiceService.createInvoice(fatura);

              if (response && response.success && response.data) {
                // Verificar se houve erro no gateway
                if (response.error) {
                  statusElement.textContent = 'Criada com erro ⚠️';
                  statusElement.style.color = '#ff9800';
                  faturaElement.style.background = '#fff3e0';
                  sucessos++;
                  erros++; // Contar como sucesso na criação mas com erro no gateway
                  faturasCriadas.push(response.data);
                } else {
                  statusElement.textContent = 'Criada ✅';
                  statusElement.style.color = '#4caf50';
                  faturaElement.style.background = '#e8f5e8';
                  sucessos++;
                  faturasCriadas.push(response.data);
                }
              } else {
                statusElement.textContent = 'Erro ❌';
                statusElement.style.color = '#f44336';
                faturaElement.style.background = '#ffebee';
                erros++;
              }

            } catch (error: any) {
              console.error(`Erro ao criar fatura ${i + 1}:`, error);
              statusElement.textContent = 'Erro ❌';
              statusElement.style.color = '#f44336';
              faturaElement.style.background = '#ffebee';
              erros++;
            }

            // Aguardar um pouco entre as criações
            await new Promise(resolve => setTimeout(resolve, 300));
          }

          // Atualizar texto final
          progressText.textContent = `Concluído! ${sucessos} criadas, ${erros} erros.`;
          progressBar.style.width = '100%';

          // Aguardar um pouco antes de fechar
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Fechar o SweetAlert
          Swal.close();

          // Mostrar resultado final
          if (erros === 0) {
            Swal.fire({
              icon: 'success',
              title: 'Criação Concluída!',
              text: `Todas as ${sucessos} faturas foram criadas com sucesso.`,
              confirmButtonText: 'OK'
            });
          } else {
            Swal.fire({
              icon: 'warning',
              title: 'Criação Concluída',
              text: `Todas as ${sucessos} faturas foram criadas com sucesso, mas com erros.`,
              confirmButtonText: 'OK'
            });
          }

          // Recarregar dados se necessário
          if (onSubmit) {
            await onSubmit(); // Chamar onSubmit para recarregar
          }

        }
      });

    } catch (error: any) {
      console.error('Erro durante criação em massa:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro na Criação',
        text: 'Ocorreu um erro durante a criação das faturas.',
        confirmButtonText: 'OK'
      });
    }
  };

  // Função para atualizar faturas com progresso
  const handleBulkUpdateWithProgress = async (faturasParaAtualizar: any[]) => {
    try {
      // Fechar o formulário primeiro
      onClose();

      // Mostrar SweetAlert com progresso
      const { value: confirmed } = await Swal.fire({
        title: 'Atualizando Faturas',
        html: `
          <div id="progress-container">
            <div style="text-align: center; margin-bottom: 20px;">
              <div class="swal2-progress-bar" style="width: 100%; height: 4px; background: #e0e0e0; border-radius: 2px; overflow: hidden;">
                <div id="progress-bar" style="width: 0%; height: 100%; background: #4caf50; transition: width 0.3s;"></div>
              </div>
              <div id="progress-text" style="margin-top: 10px; font-size: 14px; color: #666;">
                Preparando atualização...
              </div>
            </div>
            <div id="faturas-progress" style="max-height: 300px; overflow-y: auto; text-align: left;">
              ${faturasParaAtualizar.map(fatura => `
                <div id="fatura-${fatura.id}" style="padding: 8px; margin: 4px 0; border-radius: 4px; background: #f5f5f5; display: flex; justify-content: space-between; align-items: center;">
                  <span style="font-weight: 500;">Fatura #${fatura.id}</span>
                  <span id="status-${fatura.id}" style="color: #666;">Aguardando...</span>
                </div>
              `).join('')}
            </div>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: async () => {
          const progressBar = document.getElementById('progress-bar') as HTMLElement;
          const progressText = document.getElementById('progress-text') as HTMLElement;

          let sucessos = 0;
          let erros = 0;

          for (let i = 0; i < faturasParaAtualizar.length; i++) {
            const fatura = faturasParaAtualizar[i];
            const statusElement = document.getElementById(`status-${fatura.id}`) as HTMLElement;
            const faturaElement = document.getElementById(`fatura-${fatura.id}`) as HTMLElement;

            try {
              // Atualizar texto de progresso
              progressText.textContent = `Atualizando fatura #${fatura.id}...`;

              // Atualizar barra de progresso
              const progress = ((i + 1) / faturasParaAtualizar.length) * 100;
              progressBar.style.width = `${progress}%`;

              // Atualizar status da fatura
              statusElement.textContent = 'Atualizando...';
              statusElement.style.color = '#ff9800';
              faturaElement.style.background = '#fff3e0';

              // Aguardar um pouco para mostrar o progresso
              await new Promise(resolve => setTimeout(resolve, 500));

              // Fazer a chamada da API
              const response = await invoiceService.updateInvoice(fatura.id, fatura);

              if (response && response.success) {
                statusElement.textContent = 'Atualizada ✅';
                statusElement.style.color = '#4caf50';
                faturaElement.style.background = '#e8f5e8';
                sucessos++;
              } else {
                statusElement.textContent = 'Erro ❌';
                statusElement.style.color = '#f44336';
                faturaElement.style.background = '#ffebee';
                erros++;
              }

            } catch (error: any) {
              console.error(`Erro ao atualizar fatura ${fatura.id}:`, error);
              statusElement.textContent = 'Erro ❌';
              statusElement.style.color = '#f44336';
              faturaElement.style.background = '#ffebee';
              erros++;
            }

            // Aguardar um pouco entre as atualizações
            await new Promise(resolve => setTimeout(resolve, 300));
          }

          // Atualizar texto final
          progressText.textContent = `Concluído! ${sucessos} atualizadas, ${erros} erros.`;
          progressBar.style.width = '100%';

          // Aguardar um pouco antes de fechar
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Fechar o SweetAlert
          Swal.close();

          // Mostrar resultado final
          if (erros === 0) {
            Swal.fire({
              icon: 'success',
              title: 'Atualização Concluída!',
              text: `Todas as ${sucessos} faturas foram atualizadas com sucesso.`,
              confirmButtonText: 'OK'
            });
          } else {
            Swal.fire({
              icon: 'warning',
              title: 'Atualização Concluída',
              text: `${sucessos} faturas atualizadas com sucesso. ${erros} faturas com erro.`,
              confirmButtonText: 'OK'
            });
          }

          // Recarregar dados se necessário
          if (onSubmit) {
            await onSubmit(); // Chamar onSubmit para recarregar
          }

        }
      });

    } catch (error: any) {
      console.error('Erro durante atualização em massa:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro na Atualização',
        text: 'Ocorreu um erro durante a atualização das faturas.',
        confirmButtonText: 'OK'
      });
    }
  };

  return (

    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ p: 1.5, fontSize: '1.1rem', fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: 1.5 }}>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          {/* Cliente - só exibir se não foi fornecido clientId */}
          {!clientId && (
            <Grid item xs={12} md={12}>
              <ClientSelect
                value={formData.cliente_id || null}
                onChange={(clientId, client) => handleClientChange(client || null)}
                label="Cliente"
                placeholder="Digite para buscar um cliente"
                required={true}
                error={formData.cliente_id === 0}
                helperText={formData.cliente_id === 0 ? 'Cliente é obrigatório' : ''}
                size="small"
                fullWidth={true}
                onlyActive={true}
                allowClear={true}
              />
            </Grid>
          )}

          {/* Contrato */}
          <Grid item xs={12} md={4}>
            <ContractsActiveSelect
              value={formData.contrato_id || null}
              onChange={(value) => handleInputChange('contrato_id', value)}
              label="Contrato *"
              required={true}
              size="small"
              clientId={formData.cliente_id}
              disabled={!formData.cliente_id || (!!contractId && !invoice)}
              placeholder="Selecione um contrato"
              allowClear={true}
              error={!formData.contrato_id}
              helperText={!formData.cliente_id ? 'Selecione um cliente primeiro' : !formData.contrato_id ? 'Contrato é obrigatório' : ''}
            />
          </Grid>
          {/* Quantidade de Mensalidades e Proporcional - Ocultar no modo de edição */}
          {!isEditMode && (
            <>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Qtde Mensalidades"
                  type="number"
                  fullWidth
                  size="small"
                  value={qtdeMensalidades}
                  onChange={(e) => handleQtdeMensalidadesChange(Number(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 12 }}
                //helperText="Quantidade de mensalidades a serem geradas (1-12)"
                />
              </Grid>
            </>
          )}
          {/* Tipo */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo *</InputLabel>
              <Select
                value={formData.tipo}
                onChange={(e) => handleInputChange('tipo', e.target.value)}
                label="Tipo *"
                size="small"
              >
                <MenuItem value="mensalidade">Mensalidade</MenuItem>
                <MenuItem value="servicos">Serviços</MenuItem>
                <MenuItem value="outros">Outros</MenuItem>
              </Select>
            </FormControl>
          </Grid>



          {/* Gateway de Pagamento e Flag de Nota - Ocultar no modo de edição */}
          {!isEditMode && (
            <>
              {/* Seleção de Créditos Disponíveis (apenas para criação) */}
              {!invoice && availableCredits.length > 0 && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Usar Créditos Disponíveis</InputLabel>
                    <Select
                      multiple
                      value={formData.selectedCredits || []}
                      onChange={(e) => handleCreditSelection(e.target.value as number[])}
                      label="Usar Créditos Disponíveis"
                      renderValue={(selected) => {
                        const selectedCredits = availableCredits.filter(credit => 
                          (selected as number[]).includes(credit.id)
                        );
                        const totalValue = selectedCredits.reduce((sum, credit) => sum + Number(credit.saldo), 0);
                        return `${selectedCredits.length} crédito(s) selecionado(s) - Total: R$ ${totalValue.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`;
                      }}
                    >
                      {availableCredits.map((credit) => (
                        <MenuItem key={credit.id} value={credit.id}>
                          <Checkbox 
                            checked={(formData.selectedCredits || []).includes(credit.id)} 
                            size="small"
                          />
                          <Box>
                            <Typography variant="body2">
                              {`R$ ${Number(credit.saldo).toLocaleString('pt-BR', {minimumFractionDigits: 2})} - ${clienteCreditoService.formatTipoCredito(credit.tipo)}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {clienteCreditoService.formatDate(credit.createdAt)}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                      O valor dos créditos será abatido na primeira fatura gerada.
                    </Typography>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <GatewaySelect
                  value={formData.gateway_id || undefined}
                  onChange={handleGatewayChange}
                  label="Gateway de Pagamento"
                  required={false}
                  helperText="Gateway opcional para geração automática de boletos/PIX"
                  showOnlyActive={true}
                  size="small"
                />
              </Grid>

              {/* Mostrar desconto aplicado */}
              {!invoice && creditDiscount > 0 && (
                <Grid item xs={12}>
                  <Alert severity="success" icon={<AccountBalanceWallet />} sx={{ py: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Desconto de Créditos: </strong>
                      R$ {creditDiscount.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                    </Typography>
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12} md={6} display="flex" alignItems="center">
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.naogeranfcom === false}
                    onChange={(e) => handleInputChange('naogeranfcom', e.target.checked ? false : true)}
                  />}
                  label="Não gerar NFCom"
                />
              </Grid>
            </>
          )}
          {/* Alert informativo sobre detecção automática de proporcional */}
          {infoProporcional && formData.contrato_id && (
            <Grid item xs={12} md={8}>
              <Alert severity={isFirstInvoice ? 'info' : 'success'} sx={{ py: 0.5 }}>
                {isFirstInvoice ? '🔍 ' : '📅 '}{infoProporcional}
              </Alert>
            </Grid>
          )}

          {/* Tabela de Mensalidades - sempre mostrar */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>
              {isEditMode ? 'Faturas Selecionadas' : `Mensalidades (${qtdeMensalidades})`}
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }} elevation={1}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>{isEditMode ? 'ID' : 'Num.'}</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>Data Vencimento</TableCell>
                    <TableCell sx={{ width: '160px', py: 0.5, px: 1, fontSize: '0.8rem' }}>Valor</TableCell>
                    <TableCell sx={{ width: '160px', py: 0.5, px: 1, fontSize: '0.8rem' }}>Acréscimo</TableCell>
                    <TableCell sx={{ width: '160px', py: 0.5, px: 1, fontSize: '0.8rem' }}>Desconto</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>Descrição</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mensalidades.map((mensalidade, index) => (
                    <TableRow key={mensalidade.id}>
                      <TableCell sx={{ width: '50px', py: 0.5, px: 1, fontSize: '0.8rem' }}>{isEditMode ? mensalidade.id : index + 1}</TableCell>
                      <TableCell sx={{ width: '120px', py: 0.5, px: 1 }}>
                        <TextField
                          type="date"
                          size="small"
                          value={mensalidade.data_vencimento}
                          onChange={(e) => handleMensalidadeChange(index, 'data_vencimento', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '160px', py: 0.5, px: 1 }}>
                        <TextField
                          size="small"
                          value={mensalidade.valorFormatado}
                          onChange={(e) => handleMensalidadeChange(index, 'valorFormatado', e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '160px', py: 0.5, px: 1 }}>
                        <TextField
                          size="small"
                          value={mensalidade.acrescimoFormatado}
                          onChange={(e) => handleMensalidadeChange(index, 'acrescimoFormatado', e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '160px', py: 0.5, px: 1 }}>
                        <TextField
                          size="small"
                          value={mensalidade.descontoFormatado}
                          onChange={(e) => handleMensalidadeChange(index, 'descontoFormatado', e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={mensalidade.descricao}
                          onChange={(e) => handleMensalidadeChange(index, 'descricao', e.target.value)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Total das mensalidades */}
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem' }}>
                Total das Mensalidades: R$ {formatarMoeda(calcularTotalMensalidades())}
              </Typography>
            </Box>
          </Grid>

          {/* Informações de Pagamento - Mostrar apenas quando há pagamentos e quantidade = 1 */}
          {paymentSummary && paymentSummary.payments.length > 0 && qtdeMensalidades === 1 && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Informações de Pagamento
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Valor Total da Fatura
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {invoiceService.formatarValor(paymentSummary.totalAmount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Já Pago
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {invoiceService.formatarValor(paymentSummary.totalPaid)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Valor Restante
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color={paymentSummary.remainingAmount > 0 ? 'warning.main' : 'success.main'}
                    >
                      {invoiceService.formatarValor(paymentSummary.remainingAmount)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Status da fatura */}
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={
                      paymentSummary.isPaid
                        ? 'Fatura Paga'
                        : paymentSummary.isPartiallyPaid
                          ? 'Parcialmente Paga'
                          : 'Em Aberto'
                    }
                    color={
                      paymentSummary.isPaid
                        ? 'success'
                        : paymentSummary.isPartiallyPaid
                          ? 'warning'
                          : 'default'
                    }
                    size="small"
                  />
                </Box>

                {/* Lista de pagamentos */}
                {paymentSummary.payments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Pagamentos Realizados:
                    </Typography>
                    <List dense>
                      {paymentSummary.payments.map((payment, index) => (
                        <React.Fragment key={payment.id}>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2">
                                    {payment.forma_pagamento} - {invoiceService.formatarData(payment.data_pagamento)}
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold" color="success.main">
                                    {invoiceService.formatarValor(payment.valor_pago)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < paymentSummary.payments.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          {loading ? 'Salvando...' : isEditMode ? 'Atualizar Faturas' : `Gerar ${qtdeMensalidades} Mensalidade${qtdeMensalidades > 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceMultipleForm; 